import React, { useState } from 'react';
import { Box, Paper, TextField, Button, Typography, Alert, CircularProgress, Link } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { requestPasswordReset } from '../services/api';

const validationSchema = Yup.object({
  email: Yup.string()
    .email('Ingresa un email válido')
    .required('El email es obligatorio'),
});

// Nota de seguridad: nunca mostramos si el email existe o no, ni el
// token en pantalla (la versión vieja de este componente lo hacía —
// era un placeholder de demo que nunca se conectó a un backend real).
// El backend siempre responde el mismo mensaje genérico y, si el email
// existe, manda el link por correo.
const GENERIC_MESSAGE = 'Si el email existe en PCM, te enviamos un enlace para restablecer tu contraseña. Revisa tu bandeja de entrada (y spam).';

const ForgotPasswordForm = () => {
  const [successMessage, setSuccessMessage] = useState('');

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        formik.setSubmitting(true);
        await requestPasswordReset(values.email);
      } catch (error) {
        // Aunque falle (red, 500, etc.) mostramos el mismo mensaje: no
        // revelar nunca si el email existe.
        console.error('Error solicitando reset de contraseña:', error);
      } finally {
        setSuccessMessage(GENERIC_MESSAGE);
        formik.setSubmitting(false);
      }
    },
  });

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: '400px',
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Recuperar contraseña
        </Typography>
        <Typography variant="body1" gutterBottom align="center" sx={{ mb: 3 }}>
          Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña.
        </Typography>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        {!successMessage && (
          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              id="email"
              name="email"
              label="Email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              margin="normal"
            />

            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              disabled={formik.isSubmitting}
              sx={{ mt: 3 }}
            >
              {formik.isSubmitting ? (
                <>
                  <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                  Enviando...
                </>
              ) : (
                'Enviar enlace de recuperación'
              )}
            </Button>
          </form>
        )}

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Link href="/login" variant="body2">
            Volver a iniciar sesión
          </Link>
        </Box>
      </Paper>
    </Box>
  );
};

export default ForgotPasswordForm;
